import {
  Entity,
  EntityRelation,
  parseEntityRef,
} from '@backstage/catalog-model';
import { CatalogApi, catalogApiRef } from '@backstage/plugin-catalog-react';
import { ApiUtil } from '../../data-manipulation';
import { ExtendedUserEntity, UserEntityService } from './UserEntity';
import { useApi } from '@backstage/core-plugin-api';

/**
 * Options for configuring product service behavior.
 */
export interface ProductServiceOptions {
  /**
   * Whether to fetch the full entity details.
   * If `true`, retrieves the complete entity; otherwise, only selected fields.
   */
  fullEntity?: boolean;

  /**
   * Whether to include product members in the response.
   */
  withMembers?: boolean;

  /**
   * Whether to include roles for product members in the response.
   */
  withRoles?: boolean;
}

/**
 * Interface defining methods for interacting with product entities.
 */
export interface IProductEntityService {
  /**
   * Fetches all product entities.
   *
   * @param options - Options to configure the fields or detail level of the response.
   * @returns A promise resolving to an array of product entities.
   */
  getAll: (options?: ProductServiceOptions) => Promise<Entity[]>;

  /**
   * Fetches a specific product entity by reference.
   *
   * @param entity - The entity reference for the product.
   * @param options - Options to configure the response, such as including members.
   * @returns A promise resolving to a single product entity or `undefined` if not found.
   */
  byReference: (
    entity: Entity,
    options?: ProductServiceOptions,
  ) => Promise<ProductEntity | undefined>;

  /**
   * Fetches product entities related to a given entity.
   *
   * @param entity - The entity for which related products are being fetched.
   * @param options - Options to configure the response, such as including members.
   * @returns A promise resolving to an array of related product entities.
   */
  byEntity: (
    entity?: Entity,
    options?: ProductServiceOptions,
  ) => Promise<ProductEntity[]>;

  /**
   * Fetches product entities related to the given entities.
   *
   * @param entity - A collection of entities for which related products are being fetched.
   * @param options - Options to configure the response, such as including members.
   * @returns A promise resolving to an array of related product entities.
   */
  byEntities: (
    entities: Entity[],
    options?: ProductServiceOptions,
  ) => Promise<ProductEntity[]>;
}

/**
 * Extends a product entity with additional information about members.
 */
export interface ProductEntity extends Entity {
  /**
   * Members associated with the product.
   */
  members?: ExtendedUserEntity[];
}

/**
 * Fields to fetch when querying for products by default.
 */
const productFields = [
  'metadata.name',
  'metadata.uid',
  'spec.members',
  'metadata.namespace',
  'metadata.title',
];

/**
 * Service class for interacting with product entities in the catalog.
 *
 * @category CatalogServices
 */
export class ProductEntityService implements IProductEntityService {
  private catalogApi: CatalogApi;

  /**
   * Creates an instance of the ProductEntityService.
   *
   * @param catalogApi - An instance of the Catalog API for querying entities.
   */
  constructor(catalogApi: CatalogApi) {
    this.catalogApi = catalogApi;
  }

  /**
   * Maps product entities to include their members.
   *
   * @param productEntities - The product entities to map.
   * @returns A promise resolving to an array of products with member information.
   */
  private async mapProductMembers(productEntities: Entity[]) {
    const userService = new UserEntityService(this.catalogApi);
    const mapMembers = productEntities.map(async product => {
      const productMembers = await userService.byEntity(product);
      return { ...product, members: productMembers };
    });

    return await Promise.all(mapMembers);
  }

  /**
   * Fetches all product entities.
   *
   * @param options - Options to configure the fields or detail level of the response.
   * @returns A promise resolving to an array of product entities.
   */
  async getAll(options?: ProductServiceOptions) {
    const entities = await this.catalogApi.getEntities({
      filter: { kind: 'Product' },
      fields: options?.fullEntity ? undefined : productFields,
    });

    return entities.items || [];
  }

  async getProductsFromRelations(
    relations: EntityRelation[],
    options?: ProductServiceOptions,
  ) {
    const productRelationsFilter = relations.reduce<any[]>((acc, relation) => {
      const entityRef = parseEntityRef(relation.targetRef);
      if (entityRef.kind.toLowerCase() !== 'product') {
        return acc;
      }

      acc.push({
        kind: entityRef.kind,
        'metadata.namespace': entityRef.namespace,
        'metadata.name': entityRef.name,
      });

      return acc;
    }, []);

    if (productRelationsFilter.length === 0) {
      return [];
    }

    const products = await this.catalogApi.getEntities({
      filter: productRelationsFilter,
      fields: options?.fullEntity ? undefined : productFields,
    });

    if (options?.withMembers) {
      return await this.mapProductMembers(products.items);
    }

    return ApiUtil.getUniqueRecords(products.items) as ProductEntity[];
  }

  /**
   * Fetches product entities related to a given entity.
   *
   * @param entity - The entity for which related products are being fetched.
   * @param options - Options to configure the response, such as including members.
   * @returns A promise resolving to an array of related product entities.
   */
  async byEntity(entity?: Entity, options?: ProductServiceOptions) {
    if (!entity) {
      return [];
    }

    const relatedEntities = ApiUtil.getUniqueTargetRefs(entity.relations);
    return this.getProductsFromRelations(relatedEntities, options);
  }

  /**
   * Fetches product entities related to the given entities.
   *
   * @param entity - A collection of entities for which related products are being fetched.
   * @param options - Options to configure the response, such as including members.
   * @returns A promise resolving to an array of related product entities.
   */
  async byEntities(entities: Entity[], options?: ProductServiceOptions) {
    const validEntities = entities.filter(entity => entity !== undefined);

    const relatedEntities: EntityRelation[] = [];
    for (const entity of validEntities) {
      const relations = ApiUtil.getUniqueTargetRefs(entity.relations);
      relatedEntities.push(...relations);
    }

    return this.getProductsFromRelations(relatedEntities, options);
  }

  /**
   * Fetches a specific product entity by reference.
   *
   * @param entity - The entity reference for the product.
   * @param options - Options to configure the response, such as including members.
   * @returns A promise resolving to a single product entity or `undefined` if not found.
   */
  async byReference(entity: Entity, options?: ProductServiceOptions) {
    let product = (await this.catalogApi.getEntityByRef({
      ...ApiUtil.extractProductEntity(entity),
      kind: 'Product',
    })) as ProductEntity;

    if (product && options?.withMembers) {
      const userService = new UserEntityService(this.catalogApi);
      const productMembers = await userService.byEntity(product, {
        withRole: options.withRoles,
      });
      product = { ...product, members: productMembers };
    }

    return product;
  }
}

/**
 * React hook for creating an instance of the `ProductEntityService`.
 *
 * @returns {IProductEntityService} An instance of `ProductEntityService` bound to the catalog API.
 *
 * @category CatalogServices
 */
export const useProductEntity = (): IProductEntityService => {
  return new ProductEntityService(useApi(catalogApiRef));
};
